import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import StacksIcon from '@/public/icons/approval/stacks.svg';
import StackIcon from '@/public/icons/approval/stack.svg';
import ChevronRightIcon from '@/public/icons/approval/chevron-right.svg';
import ParentApprovalDropdown from './ParentApprovalDropdown';
import { ParentChipAnchor } from './ParentApprovalDropdown/styles';
import {
  BreadcrumbIconWrap,
  BreadcrumbInner,
  BreadcrumbSeparator,
  BreadcrumbText,
  CategoryBreadcrumbRow,
  CategoryChip,
  CategoryChipButton,
  ExtraCountBadge,
} from './styles';
import type { DocumentStatus } from './types';

export type BreadcrumbParent = {
  docId: string;
  subject: string;
};

/**
 * 상단 브래드크럼 영역 (2026 Portal Design v1.1) — parents 유무/개수에 따라 chip 구성 분기.
 * parents 가 있으면 chip 클릭 시 ParentApprovalDropdown 노출, 항목은 새 창으로 열림.
 */

type CategoryBreadcrumbProps = {
  subject: string;
  status: DocumentStatus;
  /** 연관품의(parents) 비동기 로딩 중 — 경로 영역만 스켈레톤으로 대체. */
  loading?: boolean;
  parents?: BreadcrumbParent[];
  /** 회수 노출 여부 — 조건 계산은 부모(ApprovalView) 소관. 미전달 시 false (fail closed). */
  canRecall?: boolean;
  /** 회수 버튼 클릭 시 회수 사유 입력 모달을 여는 핸들러. 미전달 시 no-op. */
  onRecall?: () => void;
  /** 반려요청 노출 여부 — 회수와 배타적. 조건 계산은 부모(ApprovalView) 소관. */
  canRequestReturn?: boolean;
  /** 반려요청 버튼 클릭 시 사유 입력 모달을 여는 핸들러. 미전달 시 no-op. */
  onRequestReturn?: () => void;
};

const CategoryBreadcrumb = ({
  subject,
  status,
  loading = false,
  parents = [],
  canRecall = false,
  onRecall,
  canRequestReturn = false,
  onRequestReturn,
}: CategoryBreadcrumbProps) => {
  const { t } = useTranslation();
  const hasParents = parents.length > 0;
  const extraCount = Math.max(0, parents.length - 1);

  // 노출 규칙 전부 parent 의 `canRecall`/`canRequestReturn` 에 포함.
  // status 가드는 방어용으로만 유지 — 완료 문서에 두 버튼이 새어 나오지 않게 한다.
  const isOngoing = status === 'P' || status === 'H';
  const showRecall = canRecall && isOngoing;
  // 회수가 가능한 문서(결재자 전원 미결재)에서는 반려요청을 감춘다 — 두 버튼은 같은 자리를 쓴다.
  const showReturnRequest = canRequestReturn && isOngoing && !showRecall;

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const handleParentToggle = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);
  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const handleRecall = () => {
    onRecall?.();
  };

  const handleRequestReturn = () => {
    onRequestReturn?.();
  };

  return (
    <CategoryBreadcrumbRow>
      <BreadcrumbInner>
        {loading ? (
          <Skeleton variant="rect" width={220} height={28} />
        ) : hasParents ? (
          <>
            <ParentChipAnchor ref={anchorRef} truncated>
              <BreadcrumbIconWrap variant="parent">
                <StacksIcon />
              </BreadcrumbIconWrap>
              <CategoryChipButton
                variant="parent"
                type="button"
                aria-haspopup="menu"
                aria-expanded={isDropdownOpen}
                onClick={handleParentToggle}
              >
                <BreadcrumbText>{parents[0].subject}</BreadcrumbText>
                {extraCount > 0 && (
                  <ExtraCountBadge>+{extraCount}</ExtraCountBadge>
                )}
              </CategoryChipButton>
              <ParentApprovalDropdown
                open={isDropdownOpen}
                onClose={handleDropdownClose}
                parents={parents}
                anchorRef={anchorRef}
              />
            </ParentChipAnchor>
            <BreadcrumbSeparator>
              <ChevronRightIcon />
            </BreadcrumbSeparator>
            <CategoryChip variant="child" truncated>
              <BreadcrumbIconWrap variant="child">
                <StackIcon />
              </BreadcrumbIconWrap>
              <BreadcrumbText>{subject}</BreadcrumbText>
            </CategoryChip>
          </>
        ) : (
          <CategoryChip variant="parent">
            <BreadcrumbIconWrap variant="parent">
              <StacksIcon />
            </BreadcrumbIconWrap>
            <BreadcrumbText>{subject}</BreadcrumbText>
          </CategoryChip>
        )}
      </BreadcrumbInner>
      {showRecall && (
        <BoxButton variant="secondary" size="small" onClick={handleRecall}>
          {t('회수')}
        </BoxButton>
      )}
      {showReturnRequest && (
        <BoxButton
          variant="secondary"
          size="small"
          onClick={handleRequestReturn}
        >
          {t('반려요청')}
        </BoxButton>
      )}
    </CategoryBreadcrumbRow>
  );
};

export default CategoryBreadcrumb;
